// Photos are re-encoded in the browser before upload: orientation fixed, long side at most 4096 px,
// JPEG, and no metadata (a canvas re-encode drops EXIF, GPS, camera data).

export const MAX_SIDE = 4096;
export const QUALITY = 0.9;
export const THUMB_SIDE = 480;
export const THUMB_QUALITY = 0.8;

export const IMAGE_MESSAGES = {
    heic: 'Fotografiile HEIC (iPhone) nu pot fi deschise în acest browser. Pe iPhone: Setări → Cameră → Formate → „Cel mai compatibil”, sau trimiteți fotografia ca JPEG.',
    invalid: 'Fișierul ales nu este o fotografie (JPEG, PNG sau WebP).',
    memory: 'Fotografia este prea mare pentru acest dispozitiv. Încercați una cu rezoluție mai mică.',
    encode: 'Fotografia nu a putut fi pregătită. Încercați din nou.',
};

/** An error whose message is ready to show to the user (Romanian) */
export class ImageError extends Error {
    constructor(message) {
        super(message);
        this.name = 'ImageError';
    }
}

export function isHeic(file) {
    return /image\/hei[cf]/i.test((file && file.type) || '') || /\.(heic|heif)$/i.test((file && file.name) || '');
}

function readBytes(blob, length) {
    const part = length ? blob.slice(0, length) : blob;
    if (part.arrayBuffer) return part.arrayBuffer();
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(part);
    });
}

/** EXIF orientation (1..8) of a JPEG, 1 when there is none */
export function readExifOrientation(buffer) {
    const view = new DataView(buffer);
    if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return 1;
    let offset = 2;
    while (offset + 4 <= view.byteLength) {
        if (view.getUint8(offset) !== 0xff) return 1;
        const marker = view.getUint8(offset + 1);
        if (marker === 0xda || marker === 0xd9) return 1; // image data starts: no EXIF before it
        const length = view.getUint16(offset + 2);
        if (marker === 0xe1 && offset + 10 <= view.byteLength && view.getUint32(offset + 4) === 0x45786966) {
            return orientationFromTiff(view, offset + 10, Math.min(view.byteLength, offset + 2 + length));
        }
        offset += 2 + length;
    }
    return 1;
}

function orientationFromTiff(view, tiff, end) {
    if (tiff + 8 > end) return 1;
    const little = view.getUint16(tiff) === 0x4949;
    const ifd = tiff + view.getUint32(tiff + 4, little);
    if (ifd + 2 > end) return 1;
    const count = view.getUint16(ifd, little);
    for (let i = 0; i < count; i++) {
        const entry = ifd + 2 + i * 12;
        if (entry + 12 > end) return 1;
        if (view.getUint16(entry, little) === 0x0112) {
            const value = view.getUint16(entry + 8, little);
            return value >= 1 && value <= 8 ? value : 1;
        }
    }
    return 1;
}

function loadImageElement(blob) {
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.decoding = 'async';
        img.onload = () => resolve({ source: img, width: img.naturalWidth, height: img.naturalHeight, close: () => URL.revokeObjectURL(url) });
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('decode'));
        };
        img.src = url;
    });
}

async function decodeWith(kind, blob) {
    if (kind === 'bitmap') {
        const bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' });
        return { source: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close && bitmap.close() };
    }
    return loadImageElement(blob);
}

// Does this decoder already turn the picture by its EXIF tag? Checked once per decoder on a 2×1 JPEG
// tagged "rotate 90°": a decoder that applies the tag returns 1×2.
const orientationChecks = {};
function decoderAppliesOrientation(kind) {
    if (!orientationChecks[kind]) {
        orientationChecks[kind] = (async () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = 2;
                canvas.height = 1;
                const base = await canvasToBlob(canvas, 'image/jpeg', 0.5);
                const bytes = new Uint8Array(await readBytes(base));
                // APP1 "Exif" with one IFD0 entry: Orientation (0x0112) = 6
                const exif = [0xff, 0xe1, 0x00, 0x22, 0x45, 0x78, 0x69, 0x66, 0x00, 0x00, 0x4d, 0x4d, 0x00, 0x2a, 0x00, 0x00, 0x00, 0x08,
                    0x00, 0x01, 0x01, 0x12, 0x00, 0x03, 0x00, 0x00, 0x00, 0x01, 0x00, 0x06, 0x00, 0x00, 0x00, 0x00, 0x00, 0x00];
                const tagged = new Blob([bytes.subarray(0, 2), new Uint8Array(exif), bytes.subarray(2)], { type: 'image/jpeg' });
                const decoded = await decodeWith(kind, tagged);
                const applied = decoded.width === 1 && decoded.height === 2;
                decoded.close();
                return applied;
            } catch (e) {
                return true; // cannot tell: modern browsers apply it
            }
        })();
    }
    return orientationChecks[kind];
}

/** Decodes a picked file; throws ImageError with a Romanian message */
async function decodeFile(file) {
    const kinds = typeof createImageBitmap === 'function' ? ['bitmap', 'img'] : ['img'];
    for (const kind of kinds) {
        try {
            const decoded = await decodeWith(kind, file);
            if (!decoded.width || !decoded.height) {
                decoded.close();
                continue;
            }
            decoded.kind = kind;
            return decoded;
        } catch (e) {
            // try the next decoder
        }
    }
    throw new ImageError(isHeic(file) ? IMAGE_MESSAGES.heic : IMAGE_MESSAGES.invalid);
}

function canvasToBlob(canvas, type, quality) {
    return new Promise((resolve, reject) => {
        const done = blob => (blob ? resolve(blob) : reject(new ImageError(IMAGE_MESSAGES.memory)));
        if (canvas.toBlob) {
            canvas.toBlob(done, type, quality);
            return;
        }
        try {
            const data = atob(canvas.toDataURL(type, quality).split(',')[1]);
            const bytes = new Uint8Array(data.length);
            for (let i = 0; i < data.length; i++) bytes[i] = data.charCodeAt(i);
            done(new Blob([bytes], { type }));
        } catch (e) {
            reject(new ImageError(IMAGE_MESSAGES.encode));
        }
    });
}

function makeCanvas(width, height) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new ImageError(IMAGE_MESSAGES.memory);
    return { canvas, ctx };
}

/** Frees a canvas' pixels right away (iOS keeps them until GC otherwise and runs out of canvas memory) */
export function releaseCanvas(canvas) {
    if (canvas) {
        canvas.width = 0;
        canvas.height = 0;
    }
}

function fitSize(width, height, maxSide) {
    const scale = Math.min(1, maxSide / Math.max(width, height));
    return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

/**
 * The picked file drawn upright on a canvas whose long side is at most `maxSide`.
 * The caller frees it with releaseCanvas().
 */
export async function fileToCanvas(file, maxSide = MAX_SIDE, { background = '#ffffff' } = {}) {
    if (!file) throw new ImageError(IMAGE_MESSAGES.invalid);
    let orientation = 1;
    try {
        orientation = readExifOrientation(await readBytes(file, 256 * 1024));
    } catch (e) {
        orientation = 1;
    }
    const decoded = await decodeFile(file);
    try {
        const turn = orientation > 1 && !(await decoderAppliesOrientation(decoded.kind)) ? orientation : 1;
        const swap = turn >= 5;
        const upright = swap ? { width: decoded.height, height: decoded.width } : { width: decoded.width, height: decoded.height };
        const target = fitSize(upright.width, upright.height, maxSide);
        // size of the source as drawn (before turning)
        const dw = swap ? target.height : target.width;
        const dh = swap ? target.width : target.height;
        const { canvas, ctx } = makeCanvas(target.width, target.height);
        if (background) {
            ctx.fillStyle = background; // JPEG has no transparency: PNG cut-outs go on white, not black
            ctx.fillRect(0, 0, target.width, target.height);
        }
        switch (turn) {
            case 2: ctx.transform(-1, 0, 0, 1, dw, 0); break;
            case 3: ctx.transform(-1, 0, 0, -1, dw, dh); break;
            case 4: ctx.transform(1, 0, 0, -1, 0, dh); break;
            case 5: ctx.transform(0, 1, 1, 0, 0, 0); break;
            case 6: ctx.transform(0, 1, -1, 0, dh, 0); break;
            case 7: ctx.transform(0, -1, -1, 0, dh, dw); break;
            case 8: ctx.transform(0, -1, 1, 0, 0, dw); break;
            default: break;
        }
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(decoded.source, 0, 0, dw, dh);
        return canvas;
    } catch (e) {
        if (e instanceof ImageError) throw e;
        throw new ImageError(IMAGE_MESSAGES.memory);
    } finally {
        decoded.close();
    }
}

/** A smaller copy of a canvas, halving step by step so thin lines and text stay sharp */
export function downscaleCanvas(source, maxSide) {
    const target = fitSize(source.width, source.height, maxSide);
    let current = source;
    while (current.width / 2 > target.width && current.height / 2 > target.height) {
        const half = makeCanvas(Math.round(current.width / 2), Math.round(current.height / 2));
        half.ctx.imageSmoothingQuality = 'high';
        half.ctx.drawImage(current, 0, 0, half.canvas.width, half.canvas.height);
        if (current !== source) releaseCanvas(current);
        current = half.canvas;
    }
    const out = makeCanvas(target.width, target.height);
    out.ctx.imageSmoothingQuality = 'high';
    out.ctx.drawImage(current, 0, 0, target.width, target.height);
    if (current !== source) releaseCanvas(current);
    return out.canvas;
}

/**
 * A picked photo ready for POST /files: upright JPEG (quality 0.9, long side ≤ 4096 px) without metadata,
 * plus a ~480 px thumbnail (quality 0.8). Throws ImageError with a Romanian message.
 * @returns {Promise<{blob: Blob, thumb: Blob, width: number, height: number}>}
 */
export async function prepareImage(file) {
    const canvas = await fileToCanvas(file, MAX_SIDE);
    let small = null;
    try {
        const blob = await canvasToBlob(canvas, 'image/jpeg', QUALITY);
        small = downscaleCanvas(canvas, THUMB_SIDE);
        const thumb = await canvasToBlob(small, 'image/jpeg', THUMB_QUALITY);
        return { blob, thumb, width: canvas.width, height: canvas.height };
    } catch (e) {
        if (e instanceof ImageError) throw e;
        throw new ImageError(IMAGE_MESSAGES.encode);
    } finally {
        releaseCanvas(small);
        releaseCanvas(canvas);
    }
}
